import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import type { U2000SearchPayload } from '../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GenerateUrlUUID } from '../u2000Core/tools/U2000UrlHelpers';

const LOGICALUNIT_URL = 'U3149';

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
};

export interface U3149ActiveTask {
  id: string;
  taskCode?: string;
  taskDescriptionFr?: string;
  taskDescriptionEn?: string;
  /** Présent si l’API envoie le flag ; initLU ne renvoie que des tâches actives. */
  active?: boolean;
}

/** Catalogue affiché / sélectionnable : tâches actives uniquement (aligné GET /U3149/initLU). */
export function u3149FilterActiveTasksCatalog(activeTasks: U3149ActiveTask[] | undefined): U3149ActiveTask[] {
  return (activeTasks ?? []).filter((task) => task.active !== false);
}

export function u3149PrunePunchableTaskIdsToActiveCatalog(
  punchableTaskIds: string[] | undefined,
  activeTasks: U3149ActiveTask[] | undefined,
): string[] {
  const allowed = new Set(u3149FilterActiveTasksCatalog(activeTasks).map((task) => task.id));
  return [...(punchableTaskIds ?? [])].filter((id) => allowed.has(id)).sort();
}

/** Réponse GET /U3149/initLU + logicalUnitCode fusionné côté shell. */
export interface U3149InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  logicalUnitCode: string;
  activeTasks?: U3149ActiveTask[];
}

/** Ligne POST /U3149/search. */
export interface U3149WorkNatureSearchRow {
  id: string;
  workNatureCode?: string;
  sortOrder?: number;
  active?: boolean;
  workNatureDescriptionFr?: string;
  workNatureDescriptionEn?: string;
  version: number;
  punchableTaskCount?: number;
  deletedAt?: string | Date | null;
  createdAt?: string | Date;
}

export interface U3149WorkNatureTranslation {
  id?: string;
  languageCode: string;
  workNatureDescription: string;
  version?: number;
}

/** Détail GET /U3149/:id (result). */
export interface U3149WorkNature {
  id: string;
  workNatureCode: string;
  sortOrder: number;
  active: boolean;
  translations: U3149WorkNatureTranslation[];
  punchableTaskIds: string[];
  version: number;
}

export type U3149TranslationFormRow = U3149WorkNatureTranslation;

export type U3149WorkNatureFormRow = U3149WorkNature;

export interface U3149WorkNatureCreateBody {
  workNatureCode: string;
  sortOrder: number;
  active: boolean;
  translations: Array<{
    languageCode: string;
    workNatureDescription: string;
  }>;
}

export interface U3149WorkNatureUpdateBody {
  id: string;
  sortOrder: number;
  active: boolean;
  version: number;
  translations: Array<
    | {
        languageCode: string;
        workNatureDescription: string;
      }
    | {
        id: string;
        languageCode: string;
        workNatureDescription: string;
        version: number;
      }
  >;
  punchableTaskIds?: string[];
}

export interface U3149WorkNatureDeleteBody {
  id: string;
  version: number;
}

export const U3149_ERROR_CODE_DUPLICATE = 'BU314901';
export const U3149_ERROR_CODE_IN_USE = 'BU314902';
export const U3149_ERROR_CODE_INVALID_PUNCHABLE_TASKS = 'BU314903';

export function createEmptyU3149FormRow(): U3149WorkNatureFormRow {
  return {
    id: '',
    workNatureCode: '',
    sortOrder: 1,
    active: true,
    version: 0,
    translations: [
      { languageCode: 'fr', workNatureDescription: '' },
      { languageCode: 'en', workNatureDescription: '' },
    ],
    punchableTaskIds: [],
  };
}

export function normalizeU3149FromApi(row: U3149WorkNature): U3149WorkNature {
  const punchableTaskIds = Array.isArray(row.punchableTaskIds) ? [...row.punchableTaskIds].sort() : [];
  return {
    ...row,
    active: row.active !== false,
    sortOrder: typeof row.sortOrder === 'number' ? row.sortOrder : Number(row.sortOrder) || 0,
    punchableTaskIds,
    translations: Array.isArray(row.translations)
      ? row.translations.map((tr) => ({
          ...tr,
          languageCode: typeof tr.languageCode === 'string' ? tr.languageCode.trim().toLowerCase() : tr.languageCode,
        }))
      : [],
  };
}

export function u3149FormatActiveTaskLabel(task: U3149ActiveTask, language: string): string {
  const code = task.taskCode?.trim() ?? '';
  const isFr = language.startsWith('fr');
  const linguistic =
    (isFr ? task.taskDescriptionFr : task.taskDescriptionEn)?.trim() ||
    task.taskDescriptionFr?.trim() ||
    task.taskDescriptionEn?.trim() ||
    '';
  if (code && linguistic) return `${code} — ${linguistic}`;
  return code || linguistic || task.id;
}

function u3149TranslationDescription(
  translations: U3149WorkNatureTranslation[] | undefined,
  lang: 'fr' | 'en',
): string | undefined {
  if (!Array.isArray(translations)) return undefined;
  const tr = translations.find((t) => t.languageCode?.trim().toLowerCase() === lang);
  const desc = tr?.workNatureDescription?.trim();
  return desc || undefined;
}

/** Reconstruit une ligne search (descriptions FR/EN) à partir du détail fiche — évite la perte d'affichage liste après GET/save. */
export function u3149FormRowToSearchRow(
  row: U3149WorkNature | U3149WorkNatureFormRow,
  existing?: U3149WorkNatureSearchRow | null,
): U3149WorkNatureSearchRow {
  const punchableCount =
    'punchableTaskCount' in row && row.punchableTaskCount != null
      ? row.punchableTaskCount
      : Array.isArray(row.punchableTaskIds)
        ? row.punchableTaskIds.length
        : undefined;
  const searchRow: U3149WorkNatureSearchRow = {
    id: row.id,
    workNatureCode: row.workNatureCode,
    sortOrder: row.sortOrder,
    active: row.active !== false,
    workNatureDescriptionFr: u3149TranslationDescription(row.translations, 'fr'),
    workNatureDescriptionEn: u3149TranslationDescription(row.translations, 'en'),
    version: row.version,
    punchableTaskCount: punchableCount,
  };
  if (existing) {
    return { ...existing, ...searchRow };
  }
  return searchRow;
}

export function u3149FormatWorkNatureListLabel(row: U3149WorkNatureSearchRow, language: string): string {
  const code = row.workNatureCode?.trim() ?? '';
  const isFr = language.startsWith('fr');
  const linguistic =
    (isFr ? row.workNatureDescriptionFr : row.workNatureDescriptionEn)?.trim() ||
    row.workNatureDescriptionFr?.trim() ||
    row.workNatureDescriptionEn?.trim() ||
    '';
  if (code && linguistic) return `${code} — ${linguistic}`;
  return code || linguistic;
}

export function buildCreateBody(data: U3149WorkNatureFormRow): U3149WorkNatureCreateBody {
  return {
    workNatureCode: data.workNatureCode.trim(),
    sortOrder: Number(data.sortOrder),
    active: data.active !== false,
    translations: data.translations.map((tr) => ({
      languageCode: tr.languageCode.trim().toLowerCase(),
      workNatureDescription: tr.workNatureDescription.trim(),
    })),
  };
}

export function buildUpdateBody(data: U3149WorkNatureFormRow): U3149WorkNatureUpdateBody {
  return {
    id: data.id,
    sortOrder: Number(data.sortOrder),
    active: data.active !== false,
    version: data.version,
    translations: data.translations.map((tr) => {
      const languageCode = tr.languageCode.trim().toLowerCase();
      const workNatureDescription = tr.workNatureDescription.trim();
      const hasId = Boolean(tr.id && String(tr.id).trim() !== '');
      if (hasId && tr.id) {
        return {
          id: tr.id,
          languageCode,
          workNatureDescription,
          version: typeof tr.version === 'number' ? tr.version : 0,
        };
      }
      return { languageCode, workNatureDescription };
    }),
    punchableTaskIds: Array.isArray(data.punchableTaskIds) ? [...data.punchableTaskIds].sort() : [],
  };
}

export function buildDeleteBody(data: U3149WorkNatureFormRow): U3149WorkNatureDeleteBody {
  return {
    id: data.id,
    version: data.version,
  };
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, body: U3149WorkNatureCreateBody) => {
  return axios.post(LogicalUnitEndpoints.CREATE, body);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, body: U3149WorkNatureUpdateBody) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, body: U3149WorkNatureDeleteBody) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), body);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; description?: string };
  switch (apiErr?.errorCode) {
    case U3149_ERROR_CODE_DUPLICATE:
      errorResult.fieldErrors['workNatureCode'] = t('errWorkNatureCodeDuplicate', { ns: nameSpace });
      break;
    case U3149_ERROR_CODE_IN_USE:
      errorResult.genericErrors.push({
        title: t('errorTitle', { ns: nameSpace }),
        text: t('errDeleteInUse', { ns: nameSpace }),
      });
      break;
    case U3149_ERROR_CODE_INVALID_PUNCHABLE_TASKS:
      errorResult.genericErrors.push({
        title: t('errorTitle', { ns: nameSpace }),
        text: t('errInvalidPunchableTasks', { ns: nameSpace }),
      });
      break;
    default:
      break;
  }
}
